// MACHINERY V2 (Stroje) — the compact V1 card's ONE footer (M2.16b).
//
// Rendered truth: mobile/screenshots/05-v1-phone-claude.png (all three
// branches are visible across its four cards) + 08-v1-tablet-1024.png (the
// unconnected branch). Reference impl: mobile/machinery-mobile.jsx MbV1Card
// (:186-206).
//
// Split from FleetCardCompact.tsx because the card is the composition and this
// is the branch — the same seam StateCard/AlarmStrip/ProgramLine already keep
// on the desktop side, and it holds both files clear of the 300-line cap.
//
// >>> EXACTLY ONE PANEL, AND THE PRECEDENCE IS NOT DECIDED HERE. <<<
// deriveFleetActivity() (fleetActivity.ts) ranks the branches for EVERY fleet
// surface — alarm > program > no-telemetry > freshness > nothing — and this
// component renders the branch it is handed. It never tests `alarms.length`,
// `status` or `conn`, and it no longer re-tests `showAlarmStrip && alarms.length`
// either: that flag already folds the length in (cardStates.ts:277), and the
// re-test this file used to carry was the exact thing its own header forbade.
// The board's `alarm ? … : m.status === "running" ? … :` chain is deliberately
// NOT ported.
//
// THE BOARD'S THIRD BRANCH IS A FREE-TEXT JOB LINE ("Setup — výmena upínača")
// AND WE DO NOT HAVE ONE. The gateway summary carries no job field
// (fleet/fleetTelemetry.ts's header, re-read while writing this file: no
// program, no alarm text, no since/heartbeat on today's wire), and inventing
// one would be fabricated data — the same call fleetTableDataCells.tsx's
// CurrentCell made for the AKTUÁLNE column. So the branch degrades, in order,
// to: the unconnected explainer (which the board DOES draw, card 6 of board
// 08), then the freshness stamp when the wire carries one, then nothing at all.
// Rendering nothing is deliberate: an empty 20px strip under every card is
// worse than a card that simply ends.
//
// >>> IT CARRIES NO PADDING OF ITS OWN, AND THAT IS THE F1 FIX. <<< The card
// used to pad its header block `11px 13px 0` and rely on WHICHEVER trailing
// child happened to render to supply the bottom — while this footer and the
// chip row can BOTH return null, which on today's wire is the common case, not
// an edge. The card now owns one padded flex column with a gap
// (FleetCardCompact.tsx), so every child here is a bare block and the card is
// padded whether or not any of them render.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import StatusIcon from "./StatusIcon";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "./cardStates";
import { deriveFleetActivity } from "./fleetActivity";

export interface CompactCardFooterProps {
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
}

const CompactCardFooter: React.FC<CompactCardFooterProps> = ({
  presentation: p,
  telemetry: t,
}) => {
  const { translate } = useTranslation("myMachinery");
  const activity = deriveFleetActivity(p, t);

  // 1 — ALARM CALLOUT. `lastKnown` (not `staleLook`) is what marks a reading we
  // can no longer refresh: it is TRUE for `offline` too, where staleLook is
  // deliberately false. The shared derivation carries it, so this file does not
  // pick between the two.
  if (activity.kind === "alarm") {
    const alarm = activity.alarm;
    return (
      <div
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
          borderRadius: 8, minWidth: 0,
          background: "color-mix(in oklab, var(--red) 10%, var(--card))",
          border: "1px solid color-mix(in oklab, var(--red) 30%, transparent)",
        }}
      >
        <span style={{ color: "var(--red)", display: "inline-flex", flexShrink: 0 }}>
          <StatusIcon kind="alert" size={12} />
        </span>
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 11.5, fontWeight: 700,
            color: "var(--red)", flexShrink: 0,
          }}
        >
          {activity.lastKnown
            ? translate("myMachinery:v2.fleet.alarmLastKnown") + " · " + alarm.code
            : alarm.code}
        </span>
        {alarm.message && (
          <span
            style={{
              fontSize: 11.5, color: "var(--ink-2)", minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {alarm.message}
          </span>
        )}
      </div>
    );
  }

  // 2 — RUNNING PROGRAM + ELAPSED. The elapsed clause is dropped when the wire
  // does not carry one rather than printed as a zero.
  if (activity.kind === "program") {
    return (
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--ink-2)",
            minWidth: 0, whiteSpace: "nowrap", overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <span style={{ color: "var(--green)" }}>▶ </span>
          {activity.program}
        </span>
        <span style={{ flex: 1 }} />
        {activity.elapsed && (
          <span
            style={{
              fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--faint)",
              flexShrink: 0,
            }}
          >
            {activity.elapsed}
          </span>
        )}
      </div>
    );
  }

  // 3 — TEXT. Unconnected gets the explainer in FAINT (never a status colour —
  // the locked §4.3 rule); a freshness stamp gets muted; anything else renders
  // nothing at all. The explainer is the SAME string the dashed
  // NoTelemetryFooter prints on the desktop card (stateCardAtoms.tsx), so the
  // two surfaces cannot describe the same machine with two different sentences.
  const noTelemetry = activity.kind === "noTelemetry";
  const text = noTelemetry
    ? translate("myMachinery:v2.noTelemetryHint")
    : activity.kind === "freshness"
    ? activity.text
    : null;
  if (!text) return null;

  return (
    <div
      style={{
        fontSize: 12,
        color: noTelemetry ? "var(--faint)" : "var(--muted)",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        minWidth: 0,
      }}
    >
      {text}
    </div>
  );
};

export default CompactCardFooter;
